import React from 'react';
import PropTypes from 'prop-types';
import _classNames from 'classnames';
import { createStructuredSelector } from 'reselect';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { makeSelectProfile } from 'containers/Auth/selectors';
import {
  makeSelectClientLanding,
  makeSelectPageLanding,
} from 'containers/ClientLanding/selectors';
import {
  makeSelectClientDetails,
  makeSelectFirestoreStatus,
} from 'containers/Main/selectors';
import AppContainer from 'components/AppContainer';
import useSiteCopySelector from 'components/useSiteCopySelector';
import Mixpanel from 'utils/mixpanelService';

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: props => props.bgColor || '#E8F1F5',
    display: 'flex',
    height: 113,
    [theme.breakpoints.down('sm')]: {
      height: 'auto',
    },
  },
  grayscaleRoot: {
    backgroundColor: props => props.bgColor || '#EFEFEF',
  },
  benefitWrapper: {
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      paddingTop: '16px !important',
      paddingBottom: '16px !important',
    },
  },
  benefitIcon: {
    height: 42,
    width: 'auto',
    marginRight: 16,
    [theme.breakpoints.down('sm')]: {
      width: 50,
      height: 'auto',
    },
  },
  benefitText: {
    color: theme.palette.text.primary,
    [theme.breakpoints.down('1100')]: {
      fontSize: '0.875rem',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.063rem',
    },
  },
  buttonWrapper: {
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      paddingTop: '16px !important',
      paddingBottom: '16px !important',
    },
  },
  button: {
    height: 49,
    width: 145,
    paddingLeft: 4,
    paddingRight: 4,
    borderRadius: 5,
    ...theme.typography.button,
    background: props =>
      props.background ? `#${props.background}` : '#EA6810',
    fontSize: '14pt',
    '&:hover': {
      opacity: 0.8,
      background: props =>
        props.background ? `#${props.background}` : '#EA6810',
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  grayscale: {
    filter: 'grayscale(1)',
  },
}));

const stateSelector = createStructuredSelector({
  clientDetails: makeSelectClientDetails(),
  clientLanding: makeSelectClientLanding(),
  firestoreStatus: makeSelectFirestoreStatus(),
  pageLanding: makeSelectPageLanding(),
  profile: makeSelectProfile(),
});

function LandingBanner({ grayscale, isLanding, bgColor }) {
  const {
    clientDetails,
    clientLanding,
    firestoreStatus,
    pageLanding,
    profile,
  } = useSelector(stateSelector);
  const [bannerSiteCopy] = useSiteCopySelector(['banner-landing']);
  const history = useHistory();
  const location = useLocation();

  const classes = useStyles({
    background: grayscale
      ? (_get(clientDetails, 'colors') || '').replace(/\s+/g, '').split(',')[1]
      : '#EA6810',
    bgColor,
  });
  const finalLanding = isLanding ? pageLanding : clientLanding;

  if (!profile.isEmpty) return null;

  const items =
    _get(finalLanding, 'pageText.bannerItems') ||
    _get(bannerSiteCopy, 'pageCopy.items', []);

  if (_isEmpty(items)) return null;

  const callToActionLabel =
    _get(finalLanding, 'pageText.bannerCallToActionLabel') ||
    _get(bannerSiteCopy, 'pageCopy.callToActionLabel');
  const callToActionUrl =
    _get(finalLanding, 'pageText.bannerCallToActionUrl') ||
    _get(bannerSiteCopy, 'pageCopy.callToActionUrl', '/signup');

  const redirect = () => {
    const pageName = location.pathname.split('/')[1];
    const finalPageName = pageName === '/' ? 'homePage' : `${pageName}Page`;

    Mixpanel.track(`Banner Call To Action Clicked`, {
      page: finalPageName,
    });

    history.push(callToActionUrl);
  };

  return (
    <Grid
      item
      xs={12}
      className={_classNames(classes.root, {
        [classes.grayscaleRoot]: grayscale,
      })}
    >
      <AppContainer align="center">
        <Grid item xs={12}>
          <Grid container spacing={2}>
            {items.map(item => (
              <Grid
                item
                xs={12}
                sm={6}
                md
                className={_classNames(classes.benefitWrapper, {
                  [classes.grayscale]: grayscale,
                })}
                key={item.label}
              >
                <img src={item.icon} className={classes.benefitIcon} alt="" />
                <Typography
                  component="p"
                  className={classes.benefitText}
                  variant="h6"
                >
                  {item.label}
                </Typography>
              </Grid>
            ))}
            {callToActionLabel && (
              <Grid
                item
                xs={12}
                sm={6}
                md="auto"
                className={classes.buttonWrapper}
              >
                <Button
                  variant="contained"
                  color="secondary"
                  className={classes.button}
                  onClick={redirect}
                  disableRipple
                  disabled={
                    !firestoreStatus &&
                    (callToActionUrl.includes('/signup') ||
                      callToActionUrl.includes('/login'))
                  }
                >
                  {callToActionLabel}
                </Button>
              </Grid>
            )}
          </Grid>
        </Grid>
      </AppContainer>
    </Grid>
  );
}

LandingBanner.propTypes = {
  isLanding: PropTypes.bool,
  grayscale: PropTypes.bool,
  bgColor: PropTypes.string,
};

export default LandingBanner;
